import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { HD_BODYGRAPH_GATES, HD_BODYGRAPH_GATE_TO_CENTER } from '../hd-bodygraph-metadata';
import { HdBodygraphCenter } from '../hd-bodygraph.models';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import {
  MEMORY_HINT_LETTERS_PER_REQUEST,
  fuzzyMemoryMatch,
  matchesBySimilarity,
  matchesByWordOverlap,
  normalizeMemoryInput,
  revealNextChar,
} from '../_hd-memory-text-matching';

export type HdGateGuessResult = 'correct' | 'wrong' | 'ignored';

/**
 * Spelvariant. `numbers` toont per vraag het gate-nummer (klassiek),
 * `keynotes` toont per vraag beide keynotes, `mixed` kiest random per
 * vraag tussen `numbers`-gedrag of `keynotes`-gedrag (50/50). De twee
 * `memory-*`-modi tonen de cheat-sheet-kaart van de poort met respectievelijk
 * het poortnummer (`memory-poort`) of de keynotes (`memory-keynote`)
 * verborgen; de speler vult het ontbrekende deel in tekstvelden in.
 */
export type HdGateGuessingMode =
  | 'numbers'
  | 'keynotes'
  | 'mixed'
  | 'memory-poort'
  | 'memory-keynote';

/**
 * Concrete vraagweergave per poort: enkel `numbers` of `keynotes`. De
 * `mixed`-modus wordt bij het starten van het spel deterministisch
 * uitgerold naar één van deze twee per gate. De `memory-*`-modi vallen
 * buiten dit mechanisme: de per-poort-map blijft leeg en het paneel
 * raadpleegt de spelmodus rechtstreeks.
 */
export type HdGateGuessingQuestionMode = 'numbers' | 'keynotes';

/** Invoervelden in de memory-modi. */
export type HdGateGuessingMemoryField = 'gate' | 'keynote1' | 'keynote2';

/**
 * Inputs voor `submitMemoryAnswer`. Per modus is enkel een deel relevant:
 *   • `memory-poort`: `gate` (string die naar een int parset);
 *   • `memory-keynote`: `keynote1` en (indien aanwezig op de poort) `keynote2`.
 * Niet-relevante velden mogen weggelaten of leeg gelaten worden.
 */
export interface HdGateGuessingMemoryAnswerValues {
  readonly gate?: string;
  readonly keynote1?: string;
  readonly keynote2?: string;
}

/**
 * Resultaat van één `submitMemoryAnswer`-aanroep. Bij `wrong` markeert per
 * relevant veld of de fout daar zat zodat het paneel die invoer rood kan
 * kleuren. Velden die niet van toepassing zijn voor de actieve modus blijven
 * `false`. `ignored` wanneer er geen actieve memory-vraag is.
 */
export type HdGateGuessingMemoryAnswerResult =
  | { readonly kind: 'correct' }
  | {
      readonly kind: 'wrong';
      readonly gateWrong: boolean;
      readonly keynote1Wrong: boolean;
      readonly keynote2Wrong: boolean;
    }
  | { readonly kind: 'ignored' };

/**
 * Resultaat van één `validateMemoryField`-aanroep. `correct` levert tevens de
 * canonieke (juist gespelde) waarde mee zodat het paneel het invoerveld kan
 * vervangen door de officiële spelling. `wrong` houdt de huidige waarde aan.
 * `ignored` wanneer validatie nu niet mag (verkeerde modus, niet-relevant
 * veld voor de modus, of een ander veld werd reeds gevalideerd waardoor
 * enkel `submit` nog rest).
 */
export type HdGateGuessingMemoryValidationResult =
  | { readonly kind: 'correct'; readonly canonical: string }
  | { readonly kind: 'wrong' }
  | { readonly kind: 'ignored' };

/**
 * Stand-bevriezing van welke memory-velden voor de huidige poort reeds
 * succesvol gevalideerd werden. Een `true`-vlag impliceert dat het veld
 * bevroren is op zijn canonieke waarde en niet meer bewerkbaar is.
 */
export interface HdGateGuessingMemoryValidations {
  readonly gate: boolean;
  readonly keynote1: boolean;
  readonly keynote2: boolean;
}

/**
 * Optionele parameters voor {@link HdGateGuessingGameService.start}. Wanneer
 * `gates` opgegeven wordt, gebruikt het spel die lijst exact (zonder shuffle)
 * en past de queue-grootte de score-noemers en `isFinished`-controle erop aan.
 * Voor ongekende of ongeldige poortnummers vervalt het spel naar de standaard
 * volledige set. Dient voor de "sorteren"-optie bij memory-keynote die een
 * sub-range (bv. poort 10..20) op volgorde laat oefenen.
 */
export interface HdGateGuessingStartOptions {
  readonly gates?: readonly number[];
}

/**
 * Nieuwe waarden voor de memory-keynote-invoervelden na een hint-verzoek.
 * Het paneel past deze rechtstreeks toe op zijn invoer-signals zonder
 * verdere interpretatie. Velden die niet in aanmerking kwamen voor
 * onthulling krijgen hun bestaande waarde terug.
 */
export interface HdGateGuessingMemoryHintReveal {
  readonly keynote1: string;
  readonly keynote2: string;
}

export type HdGateGuessingHistoryEvent =
  | { readonly kind: 'correct'; readonly gate: number; readonly points: number; readonly elapsedMs: number }
  | { readonly kind: 'wrong'; readonly gate: number; readonly points: number }
  | { readonly kind: 'hint'; readonly gate: number; readonly points: number }
  | { readonly kind: 'skip'; readonly gate: number; readonly points: number }
  | { readonly kind: 'streak-bonus'; readonly streak: number; readonly points: number }
  | { readonly kind: 'perfect-bonus'; readonly points: number };

export type HdGateGuessingScoreLevel = 1 | 2 | 3 | 4 | 5;

/**
 * Per-poort progressie die bewaard blijft als een poort wordt overgeslagen
 * en later terugkeert. Bevat foute pogingen plus de hint-state (huidig
 * niveau, maximum aantal niveaus, en de bij eerste-zicht vastgelegde
 * willekeurige volgorde van extraNotes-hints zodat de volgorde stabiel
 * blijft over skip-en-terug heen).
 */
interface HdGateGuessingGateProgress {
  readonly wrongAttempts: number;
  readonly hintLevel: number;
  readonly hintMaxLevel: number;
  readonly hintExtraNotesOrder: readonly number[];
}

const TIMER_TICK_INTERVAL_MS = 1000;

const STREAK_BONUS_INTERVAL = 10;
const STREAK_BONUS_POINTS = 2;
const PERFECT_GAME_BONUS = 5;
const HINT_COST = 0.5;
const SKIP_COST = 0.5;
const CORRECT_POINTS = 1;
const WRONG_POINTS = -1;

const TOTAL_GATES = HD_BODYGRAPH_GATES.length;
const STREAK_BONUS_OPPORTUNITIES = Math.floor(TOTAL_GATES / STREAK_BONUS_INTERVAL);
const MAX_POSSIBLE_SCORE =
  TOTAL_GATES * CORRECT_POINTS + STREAK_BONUS_OPPORTUNITIES * STREAK_BONUS_POINTS + PERFECT_GAME_BONUS;

/**
 * Berekent de maximaal haalbare basisscore voor een spel met `gateCount`
 * poorten: 1 punt per poort + 2 punten per volle streak van 10 + perfect-bonus.
 * Wordt gebruikt om de UI-score-noemer en de score-level-ratio mee te schalen
 * wanneer een memory-keynote sub-range gespeeld wordt (zie {@link HdGateGuessingGameService.start}).
 */
function maxPossibleScoreForGateCount(gateCount: number): number {
  const streakOpportunities = Math.floor(gateCount / STREAK_BONUS_INTERVAL);
  return gateCount * CORRECT_POINTS + streakOpportunities * STREAK_BONUS_POINTS + PERFECT_GAME_BONUS;
}

interface HdGateGuessingTimeBonusAnchor {
  readonly ms: number;
  readonly bonus: number;
  readonly label: string;
}

/**
 * Anker-tabel voor de tijdsbonus. Tussen twee opeenvolgende ankers wordt
 * de bonus lineair geïnterpoleerd (en afgerond op halve punt). Het label
 * van een interval is dat van het bovenste anker (de drempel die je net
 * niet hebt gehaald). Voor tijden onder het eerste anker geldt het maximum,
 * voor tijden boven het laatste anker is er geen bonus meer.
 */
const TIME_BONUS_ANCHORS: readonly HdGateGuessingTimeBonusAnchor[] = [
  { ms: 170_000, bonus: 19, label: 'humanDesign.games.gateGuessing.bandLightning' },
  { ms: 180_000, bonus: 17, label: 'humanDesign.games.gateGuessing.bandRoadrunner' },
  { ms: 200_000, bonus: 13, label: 'humanDesign.games.gateGuessing.bandFast' },
  { ms: 225_000, bonus:  9, label: 'humanDesign.games.gateGuessing.bandSmooth' },
  { ms: 270_000, bonus:  5, label: 'humanDesign.games.gateGuessing.bandThoughtful' },
  { ms: 330_000, bonus:  1, label: 'humanDesign.games.gateGuessing.bandOwnPace' },
  { ms: 360_000, bonus:  0, label: 'humanDesign.games.gateGuessing.bandNoRush' },
];

const MAX_POSSIBLE_TIME_BONUS = TIME_BONUS_ANCHORS[0].bonus;
const MAX_POSSIBLE_SCORE_WITH_TIME_BONUS = MAX_POSSIBLE_SCORE + MAX_POSSIBLE_TIME_BONUS;

@Injectable({ providedIn: 'root' })
export class HdGateGuessingGameService {
  static readonly maxPossibleScore = MAX_POSSIBLE_SCORE;
  static readonly maxPossibleScoreWithTimeBonus = MAX_POSSIBLE_SCORE_WITH_TIME_BONUS;
  static readonly maxPossibleTimeBonus = MAX_POSSIBLE_TIME_BONUS;
  static readonly totalGates = TOTAL_GATES;

  private readonly _isActive = signal(false);
  private readonly _mode = signal<HdGateGuessingMode>('numbers');
  private readonly _perGateQuestionMode = signal<ReadonlyMap<number, HdGateGuessingQuestionMode>>(new Map());
  private readonly _queue = signal<readonly number[]>([]);
  private readonly _events = signal<readonly HdGateGuessingHistoryEvent[]>([]);
  private readonly _score = signal(0);
  private readonly _streak = signal(0);
  private readonly _wrongAttemptsForCurrentGate = signal(0);
  private readonly _hintLevelForCurrentGate = signal(0);
  private readonly _hintMaxLevelForCurrentGate = signal(0);
  private readonly _hintExtraNotesOrderForCurrentGate = signal<readonly number[]>([]);
  private readonly _hintsUsedTotal = signal(0);
  private readonly _wrongAttemptsTotal = signal(0);
  private readonly _skipsTotal = signal(0);
  private readonly _correctGatesTotal = signal(0);
  /**
   * Aantal poorten waarmee het lopende spel gestart werd. Default {@link TOTAL_GATES}
   * (volledig spel); bij een memory-keynote sub-range (zie {@link start}) wordt dit
   * de lengte van die sub-range zodat {@link isFinished}, score-noemers en
   * score-level-ratio mee schalen met het effectieve spel.
   */
  private readonly _initialQueueSize = signal(TOTAL_GATES);
  private readonly _gateProgress = new Map<number, HdGateGuessingGateProgress>();
  /**
   * Stabiele willekeurige volgorde van extraNotes per poort, vastgelegd bij
   * het eerste contact en bewaard tot een spelreset. Leeft langer dan
   * `_gateProgress` (dat opgeruimd wordt na een correcte gok), zodat de
   * history-weergave het volledige hint-blok van een afgeronde poort kan
   * tonen in dezelfde volgorde als tijdens het spel.
   */
  private readonly _extraNotesOrderByGate = new Map<number, readonly number[]>();

  private readonly _gateAccumulatedMs = new Map<number, number>();
  private readonly _completedGateTimings = signal<ReadonlyMap<number, number>>(new Map());
  private readonly _currentGateStartedAt = signal<number | null>(null);
  private readonly _gameStartedAt = signal<number | null>(null);
  private readonly _gameFinishedAt = signal<number | null>(null);
  private readonly _timerVisible = signal(false);
  private readonly _tick = signal(0);
  private tickIntervalHandle: ReturnType<typeof setInterval> | null = null;
  private readonly destroyRef = inject(DestroyRef);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /**
   * Snapshot van de inhoudstaal op het moment van `start()`. Wordt bevroren
   * tijdens een actieve game zodat een mid-game wissel van de globale taal-
   * override (cheat-sheet of presets-bar) het lopende spel niet verschuift naar
   * andere keynotes/translations. Buiten een actief spel volgt het gewoon de
   * huidige effectieve taal.
   */
  private readonly _currentLang = signal<string>(this.contentLang.effectiveLang());

  /**
   * Per-veld validatiestatus voor de huidige memory-poort. Eens een veld
   * `true` staat, kan het andere relevante veld niet meer afzonderlijk
   * gevalideerd worden (de speler moet dan submitten). Wordt gereset bij
   * elke poort-wissel via `loadGateProgress`.
   */
  private readonly _currentMemoryValidations =
    signal<HdGateGuessingMemoryValidations>({ gate: false, keynote1: false, keynote2: false });

  readonly isActive = this._isActive.asReadonly();
  readonly mode = this._mode.asReadonly();

  /**
   * Inhoudstaal die de huidige game gebruikt voor poort-translations. Bevroren
   * tijdens een actief spel; in idle-state mag de UI hierop binden om de juiste
   * cheat-sheet card-taal te kiezen voor de memory-modi.
   */
  readonly currentLang = this._currentLang.asReadonly();
  readonly events = this._events.asReadonly();
  readonly score = this._score.asReadonly();
  readonly streak = this._streak.asReadonly();
  readonly wrongAttemptsForCurrentGate = this._wrongAttemptsForCurrentGate.asReadonly();
  readonly hintLevelForCurrentGate = this._hintLevelForCurrentGate.asReadonly();
  readonly hintMaxLevelForCurrentGate = this._hintMaxLevelForCurrentGate.asReadonly();
  readonly hintExtraNotesOrderForCurrentGate = this._hintExtraNotesOrderForCurrentGate.asReadonly();
  readonly hintUsedForCurrentGate = computed(() => this._hintLevelForCurrentGate() > 0);
  /**
   * Maximaal haalbare basisscore voor het lopende spel. Schaalt met
   * {@link _initialQueueSize} zodat sub-range memory-keynote-spellen een
   * realistische score-noemer en score-level-ratio krijgen.
   */
  readonly maxPossibleScore = computed(() => maxPossibleScoreForGateCount(this._initialQueueSize()));
  /** {@link maxPossibleScore} + {@link maxPossibleTimeBonus}. */
  readonly maxPossibleScoreWithTimeBonus = computed(() => this.maxPossibleScore() + MAX_POSSIBLE_TIME_BONUS);
  readonly maxPossibleTimeBonus = MAX_POSSIBLE_TIME_BONUS;
  /** Aantal poorten in het lopende spel (volledig of sub-range). */
  readonly totalGates = this._initialQueueSize.asReadonly();

  readonly currentGate = computed<number | null>(() => {
    const queue = this._queue();
    return queue.length > 0 ? queue[0] : null;
  });

  readonly isFinished = computed(
    () => this._isActive() && this._queue().length === 0 && this._correctGatesTotal() === this._initialQueueSize(),
  );

  readonly isPerfectGame = computed(
    () =>
      this.isFinished() &&
      this._wrongAttemptsTotal() === 0 &&
      this._hintsUsedTotal() === 0 &&
      this._skipsTotal() === 0,
  );

  readonly revealedGates = computed<ReadonlySet<number>>(
    () => new Set(
      this._events()
        .filter((event): event is Extract<HdGateGuessingHistoryEvent, { kind: 'correct' }> => event.kind === 'correct')
        .map(event => event.gate),
    ),
  );

  readonly currentGateCenter = computed<HdBodygraphCenter | null>(() => {
    const gate = this.currentGate();
    return gate == null ? null : HD_BODYGRAPH_GATE_TO_CENTER[gate] ?? null;
  });

  /**
   * Vraagweergave voor de huidige poort. Bij `mixed`-modus is dit de bij
   * spel-start gekozen waarde voor die specifieke poort; bij `numbers` of
   * `keynotes` modus is dit dezelfde waarde voor alle poorten. In de
   * `memory-*`-modi is er geen klassieke vraagweergave en geeft dit `null`.
   */
  readonly currentQuestionMode = computed<HdGateGuessingQuestionMode | null>(() => {
    const gate = this.currentGate();
    if (gate == null) {
      return null;
    }
    return this._perGateQuestionMode().get(gate) ?? null;
  });

  /** Vlag voor het paneel: in een memory-modus toont de UI tekstvelden i.p.v. een chart-klik flow. */
  readonly isMemoryMode = computed(() => {
    const mode = this._mode();
    return mode === 'memory-poort' || mode === 'memory-keynote';
  });

  /** Validatiestatus van de memory-invoervelden voor de huidige poort. */
  readonly currentMemoryValidations = this._currentMemoryValidations.asReadonly();

  /**
   * Geeft de bij spel-start vastgelegde vraagweergave voor een specifieke
   * poort terug. Wordt gebruikt door de history-weergave om correct te kunnen
   * tonen wat de speler te zien kreeg (poortnummer of keynotes), zodat de
   * answer niet verraden wordt op fout/skip/hint-rijen in `keynotes`- en
   * `mixed`-modus. Geeft `null` wanneer er geen modus voor de poort bekend
   * is (bv. buiten een actief spel).
   */
  questionModeForGate(gate: number): HdGateGuessingQuestionMode | null {
    return this._perGateQuestionMode().get(gate) ?? null;
  }

  /**
   * Of er nog een hint kan opgevraagd worden voor de huidige poort. In
   * `numbers`/`keynotes`/`mixed`-modus geldt het bestaande hint-niveausysteem
   * gestoeld op `setHintCapacityForCurrentGate`. In `memory-poort`-modus is
   * hinten uitgeschakeld (een onthulling van 1 cijfer geeft een 2-cijferig
   * antwoord te makkelijk weg). In `memory-keynote`-modus is er geen vaste
   * cap maar heeft een hint geen zin meer wanneer alle relevante
   * keynote-velden reeds gevalideerd zijn.
   */
  readonly canRequestHint = computed(() => {
    if (!this._isActive() || this.currentGate() == null) {
      return false;
    }
    const mode = this._mode();
    if (mode === 'memory-poort') {
      return this._hintLevelForCurrentGate() < 1;
    }
    if (mode === 'memory-keynote') {
      const v = this._currentMemoryValidations();
      const required = this.requiredKeynoteFieldsForCurrentGate();
      return required.some(field => !v[field]);
    }
    return this._hintLevelForCurrentGate() < this._hintMaxLevelForCurrentGate();
  });

  readonly canSkip = computed(
    () => this._isActive() && this.currentGate() != null && this._queue().length > 1,
  );

  /**
   * Of het paneel nu een validatie-knop voor `field` mag tonen in de
   * `memory-keynote`-modus. De validatie is enkel beschikbaar zolang nog
   * géén van de relevante keynote-velden succesvol werd gevalideerd. Voor
   * `memory-poort` is er slechts één veld (`gate`) en levert validatie
   * geen meerwaarde boven `submitMemoryAnswer` op — daar geeft deze
   * methode altijd `false` terug. Velden die niet bij de actieve modus
   * horen zijn eveneens niet valideerbaar.
   */
  canValidateMemoryField(field: HdGateGuessingMemoryField): boolean {
    if (!this._isActive() || this.currentGate() == null) {
      return false;
    }
    const mode = this._mode();
    if (mode !== 'memory-keynote') {
      return false;
    }
    const required = this.requiredKeynoteFieldsForCurrentGate();
    if (!required.includes(field as 'keynote1' | 'keynote2')) {
      return false;
    }
    const v = this._currentMemoryValidations();
    return required.every(f => !v[f]);
  }

  readonly scoreLevel = computed<HdGateGuessingScoreLevel | null>(() =>
    this.isFinished() ? this.computeScoreLevel(this._score()) : null,
  );

  readonly completedGateTimings = this._completedGateTimings.asReadonly();
  readonly timerVisible = this._timerVisible.asReadonly();

  readonly currentGateElapsedMs = computed(() => {
    if (!this._isActive()) {
      return 0;
    }

    const startedAt = this._currentGateStartedAt();
    if (startedAt == null) {
      return 0;
    }

    const now = this._tick();
    const current = this.currentGate();
    const accumulated = current != null ? this._gateAccumulatedMs.get(current) ?? 0 : 0;

    return Math.max(0, now - startedAt) + accumulated;
  });

  readonly totalElapsedMs = computed(() => {
    const start = this._gameStartedAt();
    if (start == null) {
      return 0;
    }

    const end = this._gameFinishedAt() ?? this._tick();
    return Math.max(0, end - start);
  });

  readonly timeBonus = computed(() =>
    this.isFinished() ? computeTimeBonus(this.totalElapsedMs()) : 0,
  );

  readonly timeBonusLabel = computed(() =>
    this.isFinished() ? computeTimeBonusLabel(this.totalElapsedMs()) : null,
  );

  readonly scoreWithTimeBonus = computed(() => this._score() + this.timeBonus());

  constructor() {
    this.destroyRef.onDestroy(() => this.stopTimerTicker());
  }

  start(
    mode: HdGateGuessingMode = 'numbers',
    shuffleFn: (gates: readonly number[]) => readonly number[] = shuffleGatesFisherYates,
    randomFn: () => number = Math.random,
    options?: HdGateGuessingStartOptions,
  ): void {
    this.resetState();
    const queue = this.buildStartQueue(options, shuffleFn);
    this._mode.set(mode);
    this._currentLang.set(this.contentLang.effectiveLang());
    this._perGateQuestionMode.set(buildPerGateQuestionMode(queue, mode, randomFn));
    this._queue.set(queue);
    this._initialQueueSize.set(queue.length);
    this._isActive.set(true);
    this._gameStartedAt.set(this.now());
    this.beginTimingForCurrentGate();
    this.startTimerTicker();
  }

  /**
   * Bepaalt de queue voor een nieuwe game: een opgegeven (gefilterde) lijst
   * wordt as-is gebruikt zodat de speler op volgorde kan oefenen; ontbreekt
   * `options.gates` of bevat het geen geldige poorten, dan valt het spel
   * terug op de volledige geshuffelde set.
   */
  private buildStartQueue(
    options: HdGateGuessingStartOptions | undefined,
    shuffleFn: (gates: readonly number[]) => readonly number[],
  ): readonly number[] {
    const requested = options?.gates;
    if (requested && requested.length > 0) {
      const validGates = requested.filter(gate => HD_BODYGRAPH_GATES.includes(gate));
      if (validGates.length > 0) {
        return validGates;
      }
    }
    return shuffleFn(HD_BODYGRAPH_GATES);
  }

  stop(): void {
    this.resetState();
  }

  toggleTimer(): void {
    this._timerVisible.update(v => !v);
  }

  submitGuess(gate: number): HdGateGuessResult {
    if (!this._isActive() || this.isMemoryMode() || this.revealedGates().has(gate)) {
      return 'ignored';
    }

    const current = this.currentGate();
    if (current == null) {
      return 'ignored';
    }

    if (gate === current) {
      this.handleCorrectGuess(current);
      return 'correct';
    }

    this.handleWrongGuess(current);
    return 'wrong';
  }

  requestHint(): void {
    if (!this.canRequestHint() || this._mode() === 'memory-keynote') {
      return;
    }

    const current = this.currentGate()!;
    this._hintLevelForCurrentGate.update(n => n + 1);
    this._hintsUsedTotal.update(n => n + 1);
    this._score.update(s => s - HINT_COST);
    this.persistGateProgress(current);
    this.appendEvent({ kind: 'hint', gate: current, points: -HINT_COST });
  }

  /**
   * Verwerkt een memory-modus antwoord. In `memory-poort` telt enkel `gate`
   * (strikte `parseInt`-vergelijking met de huidige poort); in
   * `memory-keynote` tellen `keynote1` en, indien aanwezig op de poort,
   * `keynote2`, fuzzy gematcht via {@link fuzzyMemoryMatch}. Een veld dat
   * reeds via `validateMemoryField` werd bevroren wordt automatisch als
   * correct beschouwd. Bij een correct antwoord verloopt scoring/streak/queue
   * identiek aan de gewone klik-flow; bij een fout antwoord wordt per veld
   * aangegeven of de fout daar zat zodat het paneel die invoer rood kan
   * kleuren.
   */
  submitMemoryAnswer(values: HdGateGuessingMemoryAnswerValues): HdGateGuessingMemoryAnswerResult {
    if (!this._isActive() || !this.isMemoryMode()) {
      return { kind: 'ignored' };
    }

    const current = this.currentGate();
    if (current == null) {
      return { kind: 'ignored' };
    }

    const validations = this._currentMemoryValidations();
    const mode = this._mode();
    const info = this.gateInfoForGame(current);

    const gateWrong =
      mode === 'memory-poort' && !validations.gate && parseGateNumber(values.gate) !== current;
    const keynote1Wrong =
      mode === 'memory-keynote' &&
      !validations.keynote1 &&
      !matchesKeynoteOrAlternatives(
        values.keynote1 ?? '',
        info.translations.keynote1,
        info.translations.keynote1Alternatives,
      );
    const keynote2Wrong =
      mode === 'memory-keynote' &&
      !validations.keynote2 &&
      info.translations.keynote2 !== '' &&
      !matchesKeynoteOrAlternatives(
        values.keynote2 ?? '',
        info.translations.keynote2,
        info.translations.keynote2Alternatives,
      );

    if (!gateWrong && !keynote1Wrong && !keynote2Wrong) {
      this.handleCorrectGuess(current);
      return { kind: 'correct' };
    }

    this.handleWrongGuess(current);
    return { kind: 'wrong', gateWrong, keynote1Wrong, keynote2Wrong };
  }

  /**
   * Valideert losstaand één memory-veld in `memory-keynote`-modus. Een
   * succesvolle validatie kost geen punten, bevriest het veld op zijn
   * canonieke spelling en blokkeert verdere losse validaties van het andere
   * keynote-veld (de speler moet dat dan via `submitMemoryAnswer` afsluiten).
   * Een mislukte validatie heeft géén effect (geen score-impact, geen
   * fout-event in de geschiedenis): het paneel kleurt het veld lokaal rood
   * tot de speler verder typt. Voor `memory-poort` is `validateMemoryField`
   * niet zinvol en levert dit altijd `ignored` op.
   */
  validateMemoryField(
    field: HdGateGuessingMemoryField,
    typed: string,
  ): HdGateGuessingMemoryValidationResult {
    if (!this.canValidateMemoryField(field)) {
      return { kind: 'ignored' };
    }

    const current = this.currentGate();
    if (current == null) {
      return { kind: 'ignored' };
    }

    const info = this.gateInfoForGame(current);
    const target = field === 'keynote1' ? info.translations.keynote1 : info.translations.keynote2;
    const alternatives =
      field === 'keynote1'
        ? info.translations.keynote1Alternatives
        : info.translations.keynote2Alternatives;
    if (target === '' || !matchesKeynoteOrAlternatives(typed, target, alternatives)) {
      return { kind: 'wrong' };
    }

    this._currentMemoryValidations.update(v => ({ ...v, [field]: true }));
    return { kind: 'correct', canonical: target };
  }

  /**
   * Geeft de canonieke spelling van het gevraagde keynote-veld van de huidige
   * poort terug als autocomplete-suggestie wanneer **een van** de volgende
   * criteria voldaan is op de filler-vrije, genormaliseerde vormen:
   *   1. minstens `MEMORY_AUTOCOMPLETE_MIN_MATCHES` woorden van lengte
   *      `>= MEMORY_AUTOCOMPLETE_MIN_WORD_LENGTH` komen (in willekeurige
   *      volgorde) voor in een variant van het doelwit;
   *   2. de getypte tekst zit op `>= MEMORY_AUTOCOMPLETE_MIN_SIMILARITY`
   *      van een variant van het doelwit (Levenshtein-similariteit).
   * Geeft `null` wanneer er geen actief `memory-keynote`-spel is, geen huidige
   * poort, het veld niet bij de modus hoort (`gate`), het veld geen vertaling
   * heeft, of de getypte tekst al exact overeenkomt met de canonieke spelling.
   * Spiegelt het gedrag van de equivalente methode in de channel-game zodat
   * beide spellen dezelfde fuzzy-tolerantie hanteren.
   */
  getMemoryAutocompleteSuggestion(field: HdGateGuessingMemoryField, typed: string): string | null {
    if (!this._isActive() || this._mode() !== 'memory-keynote') {
      return null;
    }

    if (field !== 'keynote1' && field !== 'keynote2') {
      return null;
    }

    const current = this.currentGate();
    if (current == null) {
      return null;
    }

    const info = this.gateInfoForGame(current);
    const target = field === 'keynote1' ? info.translations.keynote1 : info.translations.keynote2;
    const alternatives =
      field === 'keynote1'
        ? info.translations.keynote1Alternatives
        : info.translations.keynote2Alternatives;
    if (target === '') {
      return null;
    }

    if (normalizeMemoryInput(typed) === normalizeMemoryInput(target)) {
      return null;
    }

    if (matchesByWordOverlap(typed, target) || matchesBySimilarity(typed, target)) {
      return target;
    }
    for (const alt of alternatives) {
      if (alt === '') {
        continue;
      }
      if (matchesByWordOverlap(typed, alt) || matchesBySimilarity(typed, alt)) {
        return target;
      }
    }
    return null;
  }

  /**
   * Memory-modus hint: onthult `MEMORY_HINT_LETTERS_PER_REQUEST` letters
   * verdeeld over de relevante keynote-velden. Velden die reeds gevalideerd
   * zijn of waarvoor de getypte tekst al fuzzy-correct is, komen niet in
   * aanmerking voor onthulling. Wanneer geen enkele letter onthuld kan
   * worden, wordt `null` teruggegeven en is er géén score-impact. Anders
   * worden score (`-HINT_COST`), hint-tellers en de geschiedenis identiek
   * bijgewerkt aan de gewone `requestHint` (consistent met de channel-game).
   * Hints zijn enkel beschikbaar in `memory-keynote`; in `memory-poort` geeft
   * deze methode altijd `null`.
   */
  requestMemoryHint(
    typedKeynote1: string,
    typedKeynote2: string,
  ): HdGateGuessingMemoryHintReveal | null {
    if (!this.canRequestHint() || this._mode() !== 'memory-keynote') {
      return null;
    }

    const current = this.currentGate();
    if (current == null) {
      return null;
    }

    const info = this.gateInfoForGame(current);
    const validations = this._currentMemoryValidations();
    let keynote1Value = validations.keynote1 ? info.translations.keynote1 : typedKeynote1;
    let keynote2Value = validations.keynote2 ? info.translations.keynote2 : typedKeynote2;

    const slots = computeMemoryKeynoteHintSlots(
      validations,
      typedKeynote1,
      typedKeynote2,
      info.translations.keynote1,
      info.translations.keynote1Alternatives,
      info.translations.keynote2,
      info.translations.keynote2Alternatives,
    );
    if (slots.length === 0) {
      return null;
    }

    let revealed = 0;
    for (const field of slots) {
      const target = field === 'keynote1' ? info.translations.keynote1 : info.translations.keynote2;
      const currentValue = field === 'keynote1' ? keynote1Value : keynote2Value;
      const next = revealNextChar(currentValue, target);
      if (next == null) {
        continue;
      }
      if (field === 'keynote1') {
        keynote1Value = next;
      } else {
        keynote2Value = next;
      }
      revealed++;
    }

    if (revealed === 0) {
      return null;
    }

    this._hintsUsedTotal.update(n => n + 1);
    this._score.update(s => s - HINT_COST);
    this.persistGateProgress(current);
    this.appendEvent({ kind: 'hint', gate: current, points: -HINT_COST });
    return { keynote1: keynote1Value, keynote2: keynote2Value };
  }

  /** Welke keynote-velden de huidige poort vereist in `memory-keynote`-modus. */
  private requiredKeynoteFieldsForCurrentGate(): readonly ('keynote1' | 'keynote2')[] {
    const gate = this.currentGate();
    if (gate == null) {
      return [];
    }
    const info = this.gateInfoForGame(gate);
    const required: ('keynote1' | 'keynote2')[] = [];
    if (info.translations.keynote1 !== '') {
      required.push('keynote1');
    }
    if (info.translations.keynote2 !== '') {
      required.push('keynote2');
    }
    return required;
  }

  /**
   * Centrale lookup voor poort-info tijdens een actief spel: gebruikt de
   * `*In(lang)`-variant met de bevroren {@link _currentLang} zodat alle
   * memory-validaties consistent blijven met de taal die op `start()` actief was.
   */
  private gateInfoForGame(gate: number) {
    return this.gateInfoService.gateInfoIn(gate, this._currentLang());
  }

  /**
   * Stelt de hint-capaciteit (max niveau + willekeurige extraNotes-volgorde)
   * vast voor de huidige poort. Idempotent: heeft enkel effect bij de eerste
   * oproep voor een poort. Latere oproepen (bv. wanneer een poort terugkeert
   * na een skip) doen niets, zodat de eerder vastgelegde willekeurige
   * volgorde stabiel blijft.
   */
  setHintCapacityForCurrentGate(maxLevel: number, extraNotesOrder: readonly number[]): void {
    const gate = this.currentGate();
    if (gate == null || this._gateProgress.has(gate)) {
      return;
    }

    const frozenOrder = [...extraNotesOrder];
    this._hintMaxLevelForCurrentGate.set(Math.max(0, maxLevel));
    this._hintExtraNotesOrderForCurrentGate.set(frozenOrder);
    this._extraNotesOrderByGate.set(gate, frozenOrder);
    this.persistGateProgress(gate);
  }

  /**
   * Geeft de stabiele willekeurige volgorde van extraNotes terug die voor een
   * poort werd vastgelegd via `setHintCapacityForCurrentGate`. Wordt gebruikt
   * door de history-weergave om het volledige hint-blok van een correct
   * geraden poort te tonen in dezelfde volgorde als tijdens het spel.
   * Blijft beschikbaar nadat de poort correct geraden is (waarbij de
   * in-progress `_gateProgress` opgeruimd wordt).
   */
  hintExtraNotesOrderForGate(gate: number): readonly number[] {
    return this._extraNotesOrderByGate.get(gate) ?? [];
  }

  skip(): void {
    if (!this.canSkip()) {
      return;
    }

    const current = this.currentGate()!;
    this.accumulateElapsedForCurrentGate(current);
    this.persistGateProgress(current);
    this._skipsTotal.update(n => n + 1);
    this._score.update(s => s - SKIP_COST);
    this._streak.set(0);
    this.appendEvent({ kind: 'skip', gate: current, points: -SKIP_COST });
    this._queue.update(queue => [...queue.slice(1), current]);
    this.loadGateProgress(this.currentGate());
    this.beginTimingForCurrentGate();
  }

  private handleCorrectGuess(currentGate: number): void {
    const elapsedMs = this.takeElapsedForCurrentGate(currentGate);
    this.recordCompletedGateTiming(currentGate, elapsedMs);

    this._score.update(s => s + CORRECT_POINTS);
    this._correctGatesTotal.update(n => n + 1);
    this.appendEvent({ kind: 'correct', gate: currentGate, points: CORRECT_POINTS, elapsedMs });

    const newStreak = this._streak() + 1;
    this._streak.set(newStreak);

    if (newStreak % STREAK_BONUS_INTERVAL === 0) {
      this._score.update(s => s + STREAK_BONUS_POINTS);
      this.appendEvent({ kind: 'streak-bonus', streak: newStreak, points: STREAK_BONUS_POINTS });
    }

    this._gateProgress.delete(currentGate);
    this._queue.update(queue => queue.slice(1));
    this.loadGateProgress(this.currentGate());
    this.beginTimingForCurrentGate();

    if (this.isPerfectGame()) {
      this._score.update(s => s + PERFECT_GAME_BONUS);
      this.appendEvent({ kind: 'perfect-bonus', points: PERFECT_GAME_BONUS });
    }

    if (this.isFinished()) {
      this.finalizeTimer();
    }
  }

  private handleWrongGuess(currentGate: number): void {
    this._wrongAttemptsForCurrentGate.update(n => n + 1);
    this._wrongAttemptsTotal.update(n => n + 1);
    this._score.update(s => s + WRONG_POINTS);
    this._streak.set(0);
    this.persistGateProgress(currentGate);
    this.appendEvent({ kind: 'wrong', gate: currentGate, points: WRONG_POINTS });
  }

  private appendEvent(event: HdGateGuessingHistoryEvent): void {
    this._events.update(events => [event, ...events]);
  }

  private persistGateProgress(gate: number): void {
    this._gateProgress.set(gate, {
      wrongAttempts: this._wrongAttemptsForCurrentGate(),
      hintLevel: this._hintLevelForCurrentGate(),
      hintMaxLevel: this._hintMaxLevelForCurrentGate(),
      hintExtraNotesOrder: this._hintExtraNotesOrderForCurrentGate(),
    });
  }

  private loadGateProgress(gate: number | null): void {
    this._currentMemoryValidations.set({ gate: false, keynote1: false, keynote2: false });

    if (gate == null) {
      this._wrongAttemptsForCurrentGate.set(0);
      this._hintLevelForCurrentGate.set(0);
      this._hintMaxLevelForCurrentGate.set(0);
      this._hintExtraNotesOrderForCurrentGate.set([]);
      return;
    }

    const progress = this._gateProgress.get(gate);
    this._wrongAttemptsForCurrentGate.set(progress?.wrongAttempts ?? 0);
    this._hintLevelForCurrentGate.set(progress?.hintLevel ?? 0);
    this._hintMaxLevelForCurrentGate.set(progress?.hintMaxLevel ?? 0);
    this._hintExtraNotesOrderForCurrentGate.set(progress?.hintExtraNotesOrder ?? []);
  }

  private computeScoreLevel(score: number): HdGateGuessingScoreLevel {
    if (this.isPerfectGame()) {
      return 5;
    }

    const ratio = score / this.maxPossibleScore();
    if (ratio >= 0.85) return 4;
    if (ratio >= 0.65) return 3;
    if (ratio >= 0.4) return 2;
    return 1;
  }

  private resetState(): void {
    this._isActive.set(false);
    this._mode.set('numbers');
    this._perGateQuestionMode.set(new Map());
    this._queue.set([]);
    this._initialQueueSize.set(TOTAL_GATES);
    this._events.set([]);
    this._score.set(0);
    this._streak.set(0);
    this._wrongAttemptsForCurrentGate.set(0);
    this._hintLevelForCurrentGate.set(0);
    this._hintMaxLevelForCurrentGate.set(0);
    this._hintExtraNotesOrderForCurrentGate.set([]);
    this._hintsUsedTotal.set(0);
    this._wrongAttemptsTotal.set(0);
    this._skipsTotal.set(0);
    this._correctGatesTotal.set(0);
    this._gateProgress.clear();
    this._extraNotesOrderByGate.clear();
    this._currentMemoryValidations.set({ gate: false, keynote1: false, keynote2: false });
    this.resetTimerState();
  }

  private resetTimerState(): void {
    this.stopTimerTicker();
    this._gateAccumulatedMs.clear();
    this._completedGateTimings.set(new Map());
    this._currentGateStartedAt.set(null);
    this._gameStartedAt.set(null);
    this._gameFinishedAt.set(null);
    this._tick.set(0);
  }

  private beginTimingForCurrentGate(): void {
    if (this.currentGate() == null) {
      this._currentGateStartedAt.set(null);
      return;
    }
    this._currentGateStartedAt.set(this.now());
  }

  private accumulateElapsedForCurrentGate(gate: number): void {
    const startedAt = this._currentGateStartedAt();
    if (startedAt == null) {
      return;
    }
    const previous = this._gateAccumulatedMs.get(gate) ?? 0;
    this._gateAccumulatedMs.set(gate, previous + Math.max(0, this.now() - startedAt));
  }

  private takeElapsedForCurrentGate(gate: number): number {
    const startedAt = this._currentGateStartedAt();
    const live = startedAt != null ? Math.max(0, this.now() - startedAt) : 0;
    const accumulated = this._gateAccumulatedMs.get(gate) ?? 0;
    this._gateAccumulatedMs.delete(gate);
    return live + accumulated;
  }

  private recordCompletedGateTiming(gate: number, elapsedMs: number): void {
    this._completedGateTimings.update(map => {
      const next = new Map(map);
      next.set(gate, elapsedMs);
      return next;
    });
  }

  private startTimerTicker(): void {
    this.stopTimerTicker();
    this._tick.set(this.now());
    this.tickIntervalHandle = setInterval(() => this._tick.set(this.now()), TIMER_TICK_INTERVAL_MS);
  }

  private stopTimerTicker(): void {
    if (this.tickIntervalHandle != null) {
      clearInterval(this.tickIntervalHandle);
      this.tickIntervalHandle = null;
    }
  }

  private finalizeTimer(): void {
    this._gameFinishedAt.set(this.now());
    this._currentGateStartedAt.set(null);
    this.stopTimerTicker();
  }

  private now(): number {
    return Date.now();
  }
}

function shuffleGatesFisherYates(gates: readonly number[]): number[] {
  const result = [...gates];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Bouwt per-poort de concrete vraagweergave. Voor `numbers`/`keynotes` is
 * dit één en dezelfde modus voor alle poorten. Voor `mixed` wordt per
 * poort onafhankelijk een 50/50 keuze gemaakt via `randomFn` (injecteerbaar
 * voor deterministische tests).
 */
function buildPerGateQuestionMode(
  gates: readonly number[],
  mode: HdGateGuessingMode,
  randomFn: () => number,
): ReadonlyMap<number, HdGateGuessingQuestionMode> {
  const result = new Map<number, HdGateGuessingQuestionMode>();
  if (mode === 'memory-poort' || mode === 'memory-keynote') {
    return result;
  }
  for (const gate of gates) {
    result.set(gate, resolveQuestionMode(mode, randomFn));
  }
  return result;
}

function resolveQuestionMode(
  mode: Exclude<HdGateGuessingMode, 'memory-poort' | 'memory-keynote'>,
  randomFn: () => number,
): HdGateGuessingQuestionMode {
  if (mode === 'mixed') {
    return randomFn() < 0.5 ? 'numbers' : 'keynotes';
  }
  return mode;
}

/** Strikte gate-nummer parse: tolereert whitespace en niets anders. */
function parseGateNumber(input: string | undefined): number | null {
  if (input == null) {
    return null;
  }
  const trimmed = input.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  return parseInt(trimmed, 10);
}

/**
 * Bepaalt de hint-slots voor de huidige memory-keynote-vraag: welke
 * keynote-velden krijgen letters bij dit hint-verzoek? Gevalideerde velden
 * en velden waarvan de getypte tekst reeds fuzzy-correct is, worden
 * uitgesloten. Velden zonder vertaling (bv. ontbrekende `keynote2` op een
 * poort) eveneens. De resterende velden delen
 * `MEMORY_HINT_LETTERS_PER_REQUEST` letters: één per veld als beide in
 * aanmerking komen, anders alle letters in het enige overgebleven veld.
 * Geeft een lege array terug wanneer er niets te onthullen valt.
 */
function computeMemoryKeynoteHintSlots(
  validations: HdGateGuessingMemoryValidations,
  typedKeynote1: string,
  typedKeynote2: string,
  targetKeynote1: string,
  targetKeynote1Alternatives: readonly string[],
  targetKeynote2: string,
  targetKeynote2Alternatives: readonly string[],
): readonly ('keynote1' | 'keynote2')[] {
  const k1Eligible =
    targetKeynote1 !== '' &&
    !validations.keynote1 &&
    !matchesKeynoteOrAlternatives(typedKeynote1, targetKeynote1, targetKeynote1Alternatives);
  const k2Eligible =
    targetKeynote2 !== '' &&
    !validations.keynote2 &&
    !matchesKeynoteOrAlternatives(typedKeynote2, targetKeynote2, targetKeynote2Alternatives);

  if (k1Eligible && k2Eligible) {
    return ['keynote1', 'keynote2'];
  }
  if (k1Eligible) {
    return Array<'keynote1' | 'keynote2'>(MEMORY_HINT_LETTERS_PER_REQUEST).fill('keynote1');
  }
  if (k2Eligible) {
    return Array<'keynote1' | 'keynote2'>(MEMORY_HINT_LETTERS_PER_REQUEST).fill('keynote2');
  }
  return [];
}

/**
 * Fuzzy-match van getypte invoer tegen de canonieke keynote óf één van de
 * geconfigureerde alternatieven. Wordt gebruikt door zowel submit-, validatie-
 * als hint-logica zodat synoniemen overal als correct gelden.
 */
function matchesKeynoteOrAlternatives(
  typed: string,
  primary: string,
  alternatives: readonly string[],
): boolean {
  if (fuzzyMemoryMatch(typed, primary)) {
    return true;
  }
  for (const alt of alternatives) {
    if (alt !== '' && fuzzyMemoryMatch(typed, alt)) {
      return true;
    }
  }
  return false;
}

/**
 * Berekent de tijdsbonus via lineaire interpolatie tussen de ankers in
 * `TIME_BONUS_ANCHORS`. Onder het eerste anker geldt het maximum, boven
 * het laatste anker is de bonus 0. Resultaat wordt afgerond op halve punt
 * om consistent te zijn met de overige half-punt-events (hint, skip).
 */
function computeTimeBonus(elapsedMs: number): number {
  if (elapsedMs <= TIME_BONUS_ANCHORS[0].ms) {
    return TIME_BONUS_ANCHORS[0].bonus;
  }

  for (let i = 0; i < TIME_BONUS_ANCHORS.length - 1; i++) {
    const lower = TIME_BONUS_ANCHORS[i];
    const upper = TIME_BONUS_ANCHORS[i + 1];

    if (elapsedMs <= upper.ms) {
      const fraction = (elapsedMs - lower.ms) / (upper.ms - lower.ms);
      const raw = lower.bonus + fraction * (upper.bonus - lower.bonus);
      return Math.round(raw * 2) / 2;
    }
  }

  return 0;
}

/**
 * Het label van de huidige tijdsband: de eerste anker-drempel die nog niet
 * overschreden is. Boven de laatste drempel valt men in de allerlaatste band.
 */
function computeTimeBonusLabel(elapsedMs: number): string {
  for (const anchor of TIME_BONUS_ANCHORS) {
    if (elapsedMs <= anchor.ms) {
      return anchor.label;
    }
  }
  return TIME_BONUS_ANCHORS[TIME_BONUS_ANCHORS.length - 1].label;
}
